import React from 'react'
import { Field, FieldArray, Form, Formik } from 'formik'
import DeleteIcon from '@material-ui/icons/Delete'
import AddCircleIcon from '@material-ui/icons/AddCircle'

import { StyledKeyboardTimePicker } from 'civic-champs-shared/formik/components/StyledFormikDateTimePickers'
import { FormModalScreen } from 'components/FormModal'
import { FormRole, createEmptyFormRole } from 'Event/helpers/add-edit-helpers'
import { useAddEditEventStyles } from 'Event/hooks/useAddEditEventStyles'
import { InlineAddButton } from 'civic-champs-shared/core/InlineAddButton'
import { Role } from 'volunteer-role/types'
import { NumberField } from './NumberField'
import { RoleAutocompleteField } from './RoleAutocompleteField'
import { StyledInput } from 'civic-champs-shared/formik/components/StyledInput'
import { CustomSwitch } from 'civic-champs-shared/formik/components'
import yup from 'civic-champs-shared/utils/yup'
import { testEndIsAfterStart } from 'Event/validation/eventDatePayloadSchema'

export interface TemplateTimeAndRoleScreenValues {
  name: string
  isActive: boolean
  startTime: Date | null
  endTime: Date | null
  roles: FormRole[]
}

interface TemplateTimeAndRoleScreenProps {
  values: TemplateTimeAndRoleScreenValues
  onSubmit: (values: TemplateTimeAndRoleScreenValues) => void
  onBack: () => void
  //TODO should these be baked into a shared component?
  roles: Role[]
  addRole: () => Promise<Role>
}

const validationSchema = yup.object({
  name: yup.string().required('A name is required. Please enter a name.'),
  isActive: yup.bool().default(true),
  startTime: yup.date().nullable(),
  endTime: yup
    .date()
    .nullable()
    .test('min-by-time', 'The end time cannot occur before the start time', function (value) {
      const start = this.parent.startTime
      return testEndIsAfterStart(start, start, value, value)
    }),
  roles: yup
    .array()
    .of(
      yup.object({
        roleId: yup.number().typeError('Please select a Role').required('Please select a Role'),
        quantity: yup
          .number()
          .typeError('Registration limit is required')
          // @ts-ignore
          .id('Registration limit is required')
          .required('Registration limit is required'),
      }),
    )
    .notRequired(),
})

export const TemplateTimeAndRoleScreen = ({
  values,
  onSubmit,
  onBack,
  roles: allRoles,
  addRole,
}: TemplateTimeAndRoleScreenProps) => {
  const classes = useAddEditEventStyles()

  return (
    <Formik initialValues={values} validationSchema={validationSchema} onSubmit={onSubmit} validateOnChange>
      {({ values, submitForm, isSubmitting }) => {
        return (
          <Form>
            <FormModalScreen
              onNext={submitForm}
              onBack={onBack}
              nextText="Next"
              backText="Cancel"
              disabled={isSubmitting}
            >
              <div className={classes.section}>
                <Field
                  name="name"
                  component={StyledInput}
                  label={'Template Name *'}
                  placeholder="Enter the Event Template's name"
                />
                <div className={classes.switch}>
                  <Field
                    component={CustomSwitch}
                    id="isActive"
                    name="isActive"
                    thumbColor="#FFF"
                    trackColor={values.isActive ? '#008954' : '#a2a3a6'}
                    type="checkbox"
                    tooltip="Templates set to Active can be selected when creating opportunities. Templates set to Inactive will not be visible when creating new opportunities."
                  />
                  <span>{values.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className={classes.section}>
                <div className={classes.dateTimePickers}>
                  <div className={classes.dateTimePicker}>
                    <Field
                      inputVariant="outlined"
                      label="Start Time"
                      higher
                      component={StyledKeyboardTimePicker}
                      name="startTime"
                    />
                  </div>

                  <div className={classes.dateTimePicker}>
                    <Field
                      higher
                      inputVariant="outlined"
                      label="End Time"
                      component={StyledKeyboardTimePicker}
                      name="endTime"
                    />
                  </div>
                </div>
              </div>
              {/* TODO this should be a shared component, not copy-pasta! */}
              <div className={classes.section}>
                <span className={classes.sectionTitle}>Roles:</span>
                <FieldArray name="roles">
                  {({ remove, push }) => (
                    <div className={classes.section}>
                      {values.roles.map((role, index) => (
                        <div className={classes.role} key={index}>
                          <DeleteIcon
                            className={classes.roleDelete}
                            onClick={() => {
                              remove(index)
                            }}
                          />
                          <div className={classes.roleQuantity}>
                            <Field
                              name={`roles[${index}].quantity`}
                              component={NumberField}
                              label={'Max'}
                              placeholder={'Number'}
                            />
                          </div>
                          <div className={classes.roleSelector}>
                            <Field
                              name={`roles[${index}].roleId`}
                              component={RoleAutocompleteField}
                              roles={allRoles}
                              addRole={addRole}
                              label={'Role'}
                              placeholder={'Select a Role'}
                              addNewText={'Add a new role'}
                              addNewIcon={<AddCircleIcon className={classes.addIcon} />}
                            />
                          </div>
                        </div>
                      ))}
                      <InlineAddButton onClick={() => push(createEmptyFormRole())} text="Add Role" />
                    </div>
                  )}
                </FieldArray>
              </div>
            </FormModalScreen>
          </Form>
        )
      }}
    </Formik>
  )
}
